package com.speedsoftware.nibbler.ui.docs

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.ui.text.withStyle
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRow
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.CreateDocumentRequest
import com.speedsoftware.nibbler.models.DocumentDto
import com.speedsoftware.nibbler.models.ProjectDto
import com.speedsoftware.nibbler.models.UpdateDocumentRequest
import com.speedsoftware.nibbler.ui.AppState
import kotlinx.coroutines.launch

enum class DocEditorMode {
    READ,
    EDIT,
    SPLIT
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun DocumentViewerScreen(
    appState: AppState,
    project: ProjectDto
) {
    var documents by remember { mutableStateOf<List<DocumentDto>>(emptyList()) }
    var selectedDoc by remember { mutableStateOf<DocumentDto?>(null) }
    var editorMode by remember { mutableStateOf(DocEditorMode.READ) }
    var docSearchQuery by remember { mutableStateOf("") }

    var editTitle by remember { mutableStateOf("") }
    var editContent by remember { mutableStateOf("") }
    var isCreating by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(true) }
    var showTemplateMenu by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    fun loadDocs() {
        isLoading = true
        scope.launch {
            appState.apiClient.getDocuments(project.id).onSuccess {
                documents = it
                if (selectedDoc == null && it.isNotEmpty()) {
                    selectedDoc = it.first()
                    editTitle = it.first().title
                    editContent = it.first().content
                } else if (selectedDoc != null) {
                    val updated = it.firstOrNull { d -> d.id == selectedDoc?.id } ?: it.firstOrNull()
                    selectedDoc = updated
                    if (updated != null && editorMode == DocEditorMode.READ) {
                        editTitle = updated.title
                        editContent = updated.content
                    }
                }
                isLoading = false
            }.onFailure {
                isLoading = false
            }
        }
    }

    LaunchedEffect(project.id) {
        loadDocs()
    }

    val filteredDocs = documents.filter { doc ->
        docSearchQuery.isBlank() || doc.title.contains(docSearchQuery, ignoreCase = true) || doc.content.contains(docSearchQuery, ignoreCase = true)
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
    // On phones the page list and the viewer take turns instead of sitting side by side.
    val compact = maxWidth < 700.dp
    var showList by remember { mutableStateOf(true) }
    Row(modifier = Modifier.fillMaxSize().background(com.speedsoftware.nibbler.ui.theme.NibblerColors.Background)) {
        // Document Left Sidebar (Confluence-style Doc Tree)
        if (!compact || showList) Column(
            modifier = Modifier
                .then(if (compact) Modifier.fillMaxWidth() else Modifier.width(280.dp))
                .fillMaxHeight()
                .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.SurfaceContainerLow)
                .border(
                    width = 1.dp,
                    color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OutlineVariant
                )
                .padding(12.dp)
        ) {
            // Header & New Doc Button with Templates
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Knowledge Base", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurface)

                Box {
                    Button(
                        onClick = { showTemplateMenu = true },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = com.speedsoftware.nibbler.ui.theme.NibblerColors.Primary)
                    ) {
                        Text("+ Page")
                    }

                    DropdownMenu(
                        expanded = showTemplateMenu,
                        onDismissRequest = { showTemplateMenu = false },
                        modifier = Modifier.background(com.speedsoftware.nibbler.ui.theme.NibblerColors.SurfaceContainerHigh)
                    ) {
                        DropdownMenuItem(
                            text = { Text("Blank Page", color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurface) },
                            onClick = {
                                isCreating = true
                                showList = false
                                editorMode = if (compact) DocEditorMode.EDIT else DocEditorMode.SPLIT
                                editTitle = "Untitled Page"
                                editContent = "# New Page\n\nWrite your documentation here using Markdown..."
                                showTemplateMenu = false
                            }
                        )
                        DropdownMenuItem(
                            text = { Text("System Architecture Spec", color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurface) },
                            onClick = {
                                isCreating = true
                                showList = false
                                editorMode = if (compact) DocEditorMode.EDIT else DocEditorMode.SPLIT
                                editTitle = "System Architecture & Design"
                                editContent = """# System Architecture & Design

## 1. Overview
High-level description of this component, service, or system.

## 2. Architecture Diagram
- **Client Tier**: Compose Multiplatform (Web, Android, iOS)
- **API Gateway / Reverse Proxy**: Caddy Server (Automatic TLS)
- **Backend Service**: Ktor Netty Kotlin Engine
- **Persistence**: SQLite with JetBrains Exposed ORM

## 3. Security & Constraints
> [!NOTE]
> All endpoints are protected by JWT Bearer tokens and brute-force IP rate limiting.

## 4. API Endpoints
| Method | Endpoint | Description |
|---|---|---|
| POST | `/api/auth/login` | Authenticate user |
| GET | `/api/projects` | List projects |

## 5. Deployment
```bash
docker compose up -d --build
```
""".trimIndent()
                                showTemplateMenu = false
                            }
                        )
                        DropdownMenuItem(
                            text = { Text("Meeting Notes & Action Items", color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurface) },
                            onClick = {
                                isCreating = true
                                showList = false
                                editorMode = if (compact) DocEditorMode.EDIT else DocEditorMode.SPLIT
                                editTitle = "Sprint Planning Meeting Notes"
                                editContent = """# Sprint Planning Notes

**Date:** ${selectedDoc?.updatedAt?.take(10) ?: "2026-08-18"}  
**Attendees:** Team Leads, Developers, Designers

## Agenda
1. Review backlog items
2. Estimate task effort
3. Commit sprint deliverables

## Key Decisions
- Adopt standard Kanban drag-and-drop workflow.
- Introduce Sprint time intervals (2-week cadence).

## Action Items
- [ ] Implement Sprint creation API
- [ ] Connect drag & drop gestures in Compose
- [ ] Review UI responsive layouts on mobile
""".trimIndent()
                                showTemplateMenu = false
                            }
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Search input
            OutlinedTextField(
                value = docSearchQuery,
                onValueChange = { docSearchQuery = it },
                placeholder = { Text("Search docs...", color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant) },
                singleLine = true,
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth().height(56.dp)
            )

            Spacer(modifier = Modifier.height(12.dp))

            if (isLoading) {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.CenterHorizontally).padding(top = 24.dp))
            } else if (filteredDocs.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().padding(top = 24.dp), contentAlignment = Alignment.Center) {
                    Text("No pages found.", color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant, style = MaterialTheme.typography.bodySmall)
                }
            } else {
                LazyColumn(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    items(filteredDocs) { doc ->
                        val isSelected = selectedDoc?.id == doc.id
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    selectedDoc = doc
                                    editTitle = doc.title
                                    editContent = doc.content
                                    editorMode = DocEditorMode.READ
                                    isCreating = false
                                    showList = false
                                }
                                .background(if (isSelected) com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkSurfaceContainerHigh else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkSurfaceContainer)
                                .border(
                                    width = 1.dp,
                                    color = if (isSelected) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin,
                                    shape = RoundedCornerShape(8.dp)
                                )
                                .padding(12.dp)
                        ) {
                            Column {
                                Text(
                                    text = doc.title,
                                    fontFamily = FontFamily.SansSerif,
                                    fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                    color = if (isSelected) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface,
                                    fontSize = 14.sp,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                                Spacer(modifier = Modifier.height(2.dp))
                                Text(
                                    text = "UPDATED ${doc.updatedAt.take(10)}",
                                    fontFamily = FontFamily.SansSerif,
                                    fontSize = 10.sp,
                                    color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurfaceVariant
                                )
                            }
                        }
                    }
                }
            }
        }

        // Main Document Editor / Viewer Panel
        if (!compact || !showList) Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
                .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.Background)
        ) {
            // Workspace Top Toolbar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.SurfaceContainerLow)
                    .border(1.dp, com.speedsoftware.nibbler.ui.theme.NibblerColors.OutlineVariant)
                    .padding(horizontal = 16.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Breadcrumb (a back link on phones)
                if (compact) {
                    TextButton(onClick = { showList = true }) {
                        Text("All pages", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.Accent)
                    }
                } else Row(verticalAlignment = Alignment.CenterVertically) {
                    Text("Projects", style = MaterialTheme.typography.labelMedium, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant)
                    Text(" / ", style = MaterialTheme.typography.labelMedium, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant)
                    Text(project.name, style = MaterialTheme.typography.labelMedium, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant)
                    Text(" / ", style = MaterialTheme.typography.labelMedium, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant)
                    Text(if (isCreating) "New Page" else (selectedDoc?.title ?: "Select Page"), style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.Primary)
                }

                // Mode Switcher Tabs + Actions (tighter padding on phones so everything fits without scrolling)
                val toolbarPadding = if (compact) androidx.compose.foundation.layout.PaddingValues(horizontal = 10.dp, vertical = 0.dp) else ButtonDefaults.ContentPadding
                Row(
                    modifier = if (compact) Modifier.weight(1f) else Modifier,
                    horizontalArrangement = if (compact) Arrangement.spacedBy(8.dp, Alignment.End) else Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (selectedDoc != null || isCreating) {
                        OutlinedButton(
                            onClick = { editorMode = DocEditorMode.READ },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = toolbarPadding,
                            border = androidx.compose.foundation.BorderStroke(1.dp, if (editorMode == DocEditorMode.READ) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin),
                            colors = ButtonDefaults.outlinedButtonColors(
                                containerColor = if (editorMode == DocEditorMode.READ) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else Color.Transparent,
                                contentColor = if (editorMode == DocEditorMode.READ) Color.White else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                            )
                        ) {
                            Text("Preview", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                        }

                        OutlinedButton(
                            onClick = { editorMode = DocEditorMode.EDIT },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = toolbarPadding,
                            border = androidx.compose.foundation.BorderStroke(1.dp, if (editorMode == DocEditorMode.EDIT) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin),
                            colors = ButtonDefaults.outlinedButtonColors(
                                containerColor = if (editorMode == DocEditorMode.EDIT) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else Color.Transparent,
                                contentColor = if (editorMode == DocEditorMode.EDIT) Color.White else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                            )
                        ) {
                            Text("EDIT", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                        }

                        if (!compact) OutlinedButton(
                            onClick = { editorMode = DocEditorMode.SPLIT },
                            shape = RoundedCornerShape(8.dp),
                            border = androidx.compose.foundation.BorderStroke(1.dp, if (editorMode == DocEditorMode.SPLIT) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin),
                            colors = ButtonDefaults.outlinedButtonColors(
                                containerColor = if (editorMode == DocEditorMode.SPLIT) com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow else Color.Transparent,
                                contentColor = if (editorMode == DocEditorMode.SPLIT) Color.White else com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                            )
                        ) {
                            Text("Split", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                        }

                        Button(
                            onClick = {
                                scope.launch {
                                    if (isCreating) {
                                        appState.apiClient.createDocument(
                                            project.id,
                                            CreateDocumentRequest(title = editTitle, content = editContent)
                                        ).onSuccess { newDoc ->
                                            isCreating = false
                                            selectedDoc = newDoc
                                            editorMode = DocEditorMode.READ
                                            loadDocs()
                                        }
                                    } else if (selectedDoc != null) {
                                        appState.apiClient.updateDocument(
                                            project.id,
                                            selectedDoc!!.id,
                                            UpdateDocumentRequest(title = editTitle, content = editContent)
                                        ).onSuccess { updatedDoc ->
                                            selectedDoc = updatedDoc
                                            editorMode = DocEditorMode.READ
                                            loadDocs()
                                        }
                                    }
                                }
                            },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = toolbarPadding,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausCoral,
                                contentColor = Color.White
                            )
                        ) {
                            Text("SAVE", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                        }

                        if (!isCreating && selectedDoc != null) {
                            val deleteDoc: () -> Unit = {
                                scope.launch {
                                    selectedDoc?.let {
                                        appState.apiClient.deleteDocument(project.id, it.id).onSuccess {
                                            selectedDoc = null
                                            showList = true
                                            loadDocs()
                                        }
                                    }
                                }
                            }
                            if (compact) {
                                // Destructive action tucked into an overflow menu so the toolbar fits a phone
                                var showMore by remember { mutableStateOf(false) }
                                Box {
                                    Box(modifier = Modifier.size(36.dp).clickable { showMore = true }, contentAlignment = Alignment.Center) {
                                        com.speedsoftware.nibbler.ui.components.PropertyIconView(com.speedsoftware.nibbler.ui.components.PropertyIcon.DOTS, size = 18.dp)
                                    }
                                    DropdownMenu(expanded = showMore, onDismissRequest = { showMore = false }) {
                                        DropdownMenuItem(
                                            text = { Text("Delete page", color = com.speedsoftware.nibbler.ui.theme.NibblerColors.Danger) },
                                            onClick = { showMore = false; deleteDoc() }
                                        )
                                    }
                                }
                            } else {
                                OutlinedButton(
                                    onClick = deleteDoc,
                                    shape = RoundedCornerShape(8.dp),
                                    border = androidx.compose.foundation.BorderStroke(1.dp, com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausRed),
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausRed)
                                ) {
                                    Text("Delete", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                                }
                            }
                        }
                    }
                }
            }

            // Main Content Body based on Editor Mode
            if (selectedDoc == null && !isCreating) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.padding(24.dp)) {
                        Text("Knowledge Base", style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurface)
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            "Select a page from the sidebar or click '+ Page' to create new documentation.",
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.OnSurfaceVariant,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                    }
                }
            } else {
                when (editorMode) {
                    DocEditorMode.READ -> {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(24.dp)
                                .verticalScroll(rememberScrollState())
                        ) {
                            // Title & Author Meta
                            Text(
                                text = selectedDoc?.title ?: editTitle,
                                fontFamily = FontFamily.SansSerif,
                                fontWeight = FontWeight.Bold,
                                fontSize = 32.sp,
                                lineHeight = 40.sp,
                                color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                            )

                            selectedDoc?.author?.let { author ->
                                Spacer(modifier = Modifier.height(8.dp))
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Box(modifier = Modifier.size(8.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow))
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(
                                        "By ${author.name}  ·  Updated ${selectedDoc?.updatedAt?.take(10) ?: "RECENT"}",
                                        fontFamily = FontFamily.SansSerif,
                                        fontSize = 11.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurfaceVariant,
                                        letterSpacing = 0.05.sp
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(16.dp))
                            Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin))
                            Spacer(modifier = Modifier.height(20.dp))

                            // Rendered Rich Markdown
                            FormattedMarkdownView(content = selectedDoc?.content ?: editContent)
                        }
                    }

                    DocEditorMode.EDIT -> {
                        Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                            OutlinedTextField(
                                value = editTitle,
                                onValueChange = { editTitle = it },
                                label = { Text("Page title", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                                singleLine = true,
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            OutlinedTextField(
                                value = editContent,
                                onValueChange = { editContent = it },
                                label = { Text("Content (Markdown)", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.fillMaxSize()
                            )
                        }
                    }

                    DocEditorMode.SPLIT -> {
                        Row(modifier = Modifier.fillMaxSize().padding(12.dp), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            // Left: Editor
                            Column(modifier = Modifier.weight(1f).fillMaxHeight()) {
                                OutlinedTextField(
                                    value = editTitle,
                                    onValueChange = { editTitle = it },
                                    label = { Text("Page title", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                                    singleLine = true,
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier.fillMaxWidth()
                                )

                                Spacer(modifier = Modifier.height(8.dp))

                                OutlinedTextField(
                                    value = editContent,
                                    onValueChange = { editContent = it },
                                    label = { Text("Markdown", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                                    shape = RoundedCornerShape(8.dp),
                                    modifier = Modifier.fillMaxSize()
                                )
                            }

                            // Right: Live Formatted Preview
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxHeight()
                                    .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkSurfaceContainer)
                                    .border(1.dp, com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                                    .padding(16.dp)
                                    .verticalScroll(rememberScrollState())
                            ) {
                                Column {
                                    Text(
                                        text = editTitle.ifBlank { "Untitled Page" },
                                        fontFamily = FontFamily.SansSerif,
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 24.sp,
                                        color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                                    )
                                    Spacer(modifier = Modifier.height(16.dp))
                                    FormattedMarkdownView(content = editContent)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    }
}

/** Renders the inline markdown the page viewer supports: **bold** and `code`. */
private fun inlineMarkdown(text: String): androidx.compose.ui.text.AnnotatedString =
    androidx.compose.ui.text.buildAnnotatedString {
        var i = 0
        while (i < text.length) {
            val bold = text.startsWith("**", i)
            val code = !bold && text[i] == '`'
            val marker = if (bold) "**" else if (code) "`" else null
            val end = marker?.let { text.indexOf(it, i + it.length) } ?: -1
            if (marker != null && end > i + marker.length) {
                val style = if (bold) androidx.compose.ui.text.SpanStyle(fontWeight = FontWeight.Bold)
                else androidx.compose.ui.text.SpanStyle(fontFamily = FontFamily.Monospace, background = com.speedsoftware.nibbler.ui.theme.NibblerColors.SurfaceContainerHigh)
                withStyle(style) { append(text.substring(i + marker.length, end)) }
                i = end + marker.length
            } else {
                append(text[i])
                i++
            }
        }
    }

@Composable
fun FormattedMarkdownView(content: String) {
    if (content.isBlank()) {
        Text(
            "No content written yet.",
            fontFamily = FontFamily.SansSerif,
            fontSize = 13.sp,
            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurfaceVariant
        )
        return
    }

    val lines = content.lines()
    var inCodeBlock = false
    val codeBlockLines = mutableListOf<String>()

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        lines.forEach { line ->
            when {
                line.startsWith("```") -> {
                    if (inCodeBlock) {
                        // Render collected code block
                        CodeBlockCard(codeBlockLines.joinToString("\n"))
                        codeBlockLines.clear()
                        inCodeBlock = false
                    } else {
                        inCodeBlock = true
                    }
                }

                inCodeBlock -> {
                    codeBlockLines.add(line)
                }

                line.startsWith("# ") -> {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 16.dp, bottom = 4.dp)) {
                        Box(modifier = Modifier.size(12.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow))
                        Spacer(modifier = Modifier.width(10.dp))
                        Text(
                            text = line.removePrefix("# ").trim().uppercase(),
                            fontFamily = FontFamily.SansSerif,
                            fontWeight = FontWeight.Bold,
                            fontSize = 22.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface,
                            letterSpacing = 0.05.sp
                        )
                    }
                }

                line.startsWith("## ") -> {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 12.dp, bottom = 2.dp)) {
                        Box(modifier = Modifier.size(10.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausBlueLight))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = line.removePrefix("## ").trim().uppercase(),
                            fontFamily = FontFamily.SansSerif,
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                        )
                    }
                }

                line.startsWith("### ") -> {
                    Text(
                        text = line.removePrefix("### ").trim(),
                        fontFamily = FontFamily.SansSerif,
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                        color = com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausCoral
                    )
                }

                line.startsWith("- [ ]") -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            modifier = Modifier
                                .size(16.dp)
                                .background(Color.Transparent)
                                .border(1.dp, com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                        )
                        Spacer(modifier = Modifier.width(10.dp))
                        Text(
                            text = line.removePrefix("- [ ]").trim(),
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 14.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                        )
                    }
                }

                line.startsWith("- [x]") || line.startsWith("- [X]") -> {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            modifier = Modifier
                                .size(16.dp)
                                .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausBlueLight)
                                .border(1.dp, com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausBlueLight, RoundedCornerShape(8.dp)),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                "✓",
                                fontFamily = FontFamily.SansSerif,
                                fontWeight = FontWeight.Bold,
                                color = Color.White,
                                fontSize = 11.sp
                            )
                        }
                        Spacer(modifier = Modifier.width(10.dp))
                        Text(
                            text = line.removePrefix("- [x]").removePrefix("- [X]").trim(),
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 14.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurfaceVariant,
                            textDecoration = androidx.compose.ui.text.style.TextDecoration.LineThrough
                        )
                    }
                }

                line.startsWith("- ") || line.startsWith("* ") -> {
                    Row(verticalAlignment = Alignment.Top) {
                        Text(
                            "■",
                            fontSize = 10.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow,
                            modifier = Modifier.padding(end = 8.dp, top = 4.dp)
                        )
                        Text(
                            text = inlineMarkdown(line.substring(2).trim()),
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 14.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                        )
                    }
                }

                else -> {
                    if (line.isNotBlank()) {
                        Text(
                            text = inlineMarkdown(line),
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 14.sp,
                            lineHeight = 22.sp,
                            color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurface
                        )
                    }
                }
            }
        }

        if (inCodeBlock && codeBlockLines.isNotEmpty()) {
            CodeBlockCard(codeBlockLines.joinToString("\n"))
        }
    }
}

@Composable
fun CodeBlockCard(code: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkSurfaceDeep)
            .border(1.dp, com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
    ) {
        Column {
            // Terminal Window Title Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(30.dp)
                    .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkSurfaceContainerLowest)
                    .border(width = 1.dp, color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOutlineThin)
                    .padding(horizontal = 10.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    "TERMINAL / SHELL",
                    fontFamily = FontFamily.SansSerif,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    color = com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkOnSurfaceVariant
                )

                Row(horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                    Box(modifier = Modifier.size(8.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellow))
                    Box(modifier = Modifier.size(8.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausRed))
                    Box(modifier = Modifier.size(8.dp).background(com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausBlueLight))
                }
            }

            Text(
                text = code,
                fontFamily = FontFamily.SansSerif,
                fontSize = 12.sp,
                lineHeight = 18.sp,
                modifier = Modifier.padding(14.dp),
                color = com.speedsoftware.nibbler.ui.theme.NibblerColors.BauhausYellowBright
            )
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
fun CodeBlockCardPreview() {
    val sampleCode = """$ pnpm install dependencies
# 2. Run database migrations
$ pnpm run db:migrate
> Applying migration: 001_initial_schema.sql
OK"""

    Box(
        modifier = Modifier
            .background(com.speedsoftware.nibbler.ui.theme.NibblerColors.DarkBackground)
            .padding(16.dp)
    ) {
        CodeBlockCard(code = sampleCode)
    }
}

